import React, { useState, useEffect, useCallback } from 'react';
import { ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  FaExclamationTriangle, FaFire, FaUser, FaRegStar, FaRocket,
  FaCrosshairs, FaLightbulb, FaChartBar,
  FaTwitter, FaTelegram, FaTiktok, FaInstagram, FaGlobe, FaNewspaper, FaFacebookF, FaTv,
  FaBolt, FaHeart, FaComment, FaRetweet, FaEye, FaExternalLinkAlt,
  FaArrowUp, FaArrowDown, FaTimes, FaShieldAlt, FaReply, FaFlag, FaBullhorn,
  FaShareAlt, FaChartLine, FaUserPlus, FaGavel, FaCheck, FaSpinner,
} from 'react-icons/fa';
import ListsManager from '../services/ListsManager';
import ReactionModal from '../components/ReactionModal';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import OrgStrategyScreen from './OrgStrategyScreen';

let T = {};
let _t = (s) => s;

const PLATFORM_ICONS = {
  twitter: FaTwitter, telegram: FaTelegram, tiktok: FaTiktok,
  instagram: FaInstagram, facebook_group: FaFacebookF, rss: FaGlobe, webz: FaNewspaper, tv: FaTv,
};

const PLATFORM_COLORS = {
  twitter: '#1da1f2', telegram: '#0088cc', tiktok: '#ff0050',
  instagram: '#e1306c', facebook_group: '#1877F2', rss: '#22c55e', webz: '#10b981', tv: '#9f1239',
};

const CATEGORY_CONFIG = {
  urgent:            { icon: FaExclamationTriangle },
  fires:             { icon: FaFire },
  customer_issues:   { icon: FaUser },
  positive_buzz:     { icon: FaRegStar },
  opportunities:     { icon: FaRocket },
  competitive_intel: { icon: FaCrosshairs },
  product_feedback:  { icon: FaLightbulb },
  campaigns:         { icon: FaChartBar },
  monitoring:        { icon: FaEye },
};

const FRAME_LABELS = {
  complaint: 'Complaint', attack: 'Attack', praise: 'Praise',
  question: 'Question', advocacy: 'Advocacy', misinformation: 'Misinfo',
  satire: 'Satire', neutral: 'Neutral',
};

const ACTION_LABELS = {
  counter_narrative: 'Counter', react: 'React', report: 'Report',
  amplify: 'Amplify', monitor: 'Monitor', service_recovery: 'Service Recovery',
  escalate: 'Escalate', ignore: 'Ignore', trend_jack: 'Trend Jack',
  respond: 'Respond', engage: 'Engage', outreach: 'Outreach',
  respond_to_comments: 'Respond', boost_post: 'Boost', boost: 'Boost',
  adjust_strategy: 'Adjust Strategy', launch_micro_campaign: 'Campaign',
  empathetic_intervention: 'Empathize', competitive_positioning: 'Position',
  product_insight: 'Product Insight', issue_apology: 'Apologize',
  amplify_ugc: 'Amplify', routine_engagement: 'Engage', banter: 'Banter',
};

const ACTION_ICONS = {
  counter_narrative: FaShieldAlt, react: FaReply, report: FaFlag,
  amplify: FaBullhorn, share: FaShareAlt, monitor: FaEye,
  respond_to_comments: FaComment, respond: FaReply, boost_post: FaBolt,
  boost: FaBolt, adjust_strategy: FaChartLine, ignore: FaTimes,
  engage: FaReply, launch_micro_campaign: FaBullhorn,
  service_recovery: FaUser, empathetic_intervention: FaHeart,
  competitive_positioning: FaCrosshairs, product_insight: FaLightbulb,
  issue_apology: FaFlag, amplify_ugc: FaBullhorn, outreach: FaUserPlus,
  routine_engagement: FaComment, banter: FaComment, trend_jack: FaRocket,
};

const REACTION_ACTIONS = new Set([
  'react', 'service_recovery', 'empathetic_intervention', 'banter',
  'routine_engagement', 'respond_to_comments', 'issue_apology',
  'counter_narrative', 'outreach', 'trend_jack', 'respond', 'engage',
]);

const AMPLIFY_ACTIONS = new Set(['amplify_ugc', 'amplify', 'boost_post', 'boost']);

const INFORMATIONAL_ACTIONS = new Set([
  'competitive_positioning', 'product_insight', 'adjust_strategy', 'launch_micro_campaign',
]);

const REPORT_URLS = {
  twitter: {
    copyright: 'https://help.twitter.com/forms/dmca',
    trademark: 'https://help.twitter.com/forms/trademark',
    default: 'https://help.twitter.com/forms/abusiveuser',
  },
  instagram: {
    copyright: 'https://help.instagram.com/contact/552695131608132',
    trademark: 'https://help.instagram.com/contact/513588099065087',
    default: 'https://help.instagram.com/contact/383679321740945',
  },
  tiktok: {
    copyright: 'https://www.tiktok.com/legal/report/Copyright',
    trademark: 'https://www.tiktok.com/legal/report/Trademark',
    default: 'https://www.tiktok.com/legal/report/Copyright',
  },
  facebook_group: {
    copyright: 'https://www.facebook.com/help/contact/634636770043106',
    trademark: 'https://www.facebook.com/help/contact/1057766424272818',
    default: 'https://www.facebook.com/help/contact/274459462613911',
  },
};

const SORT_OPTIONS = [
  { key: 'date', labelKey: 'sortDate', defaultLabel: 'Date', defaultDir: 'desc' },
  { key: 'engagement', labelKey: 'popularity', defaultLabel: 'Popularity', defaultDir: 'desc' },
  { key: 'priority', labelKey: 'sortPriority', defaultLabel: 'Priority', defaultDir: 'desc' },
];

// ── utilities ─────────────────────────────────────────────────────────────

function timeAgo(isoDate) {
  if (!isoDate) return '';
  const ms = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function fmtNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  return `${n}`;
}

function formatAction(action) {
  return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Localized label for an action type — falls back to the English ACTION_LABELS map.
function actionLabel(action) {
  if (!action) return '';
  return _t(`app.signalFeed.action_${action}`, { defaultValue: ACTION_LABELS[action] || formatAction(action) });
}

function getActionVerb(item) {
  const action = item.action?.recommended_action || item.light_suggestion?.[0] || 'monitor';
  return actionLabel(action);
}

// ── main component ────────────────────────────────────────────────────────

export default function SignalFeedScreen() {
  const { theme } = useTheme();
  const { activeOrg } = useOrg();
  const { t, i18n } = useTranslation();
  _t = t;
  const isRTL = i18n.language === 'he';
  const orgId = activeOrg?.id;

  T = {
    bg:       theme.bg,
    card:     theme.bgCard,
    surface:  theme.bgSurface,
    border:   theme.border,
    text:     theme.textPrimary,
    textSec:  theme.textSecondary,
    textMuted: theme.textMuted,
    primary:  theme.accentBlue || '#0a84ff',
    elevated: theme.bgElevated,
    inputBg:  theme.inputBg || theme.bgSurface,
  };

  const [activeTab, setActiveTab] = useState('strategy');
  const [categories, setCategories] = useState([]);
  const [patternMap, setPatternMap] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [feedItems, setFeedItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);
  const [sortKey, setSortKey] = useState('priority');
  const [sortDir, setSortDir] = useState('desc');
  const [total, setTotal] = useState(0);
  const [sidebarW, setSidebarW] = useState(380);
  const [reactionModal, setReactionModal] = useState({ visible: false, loading: false, text: '', explanation: '', error: null, platform: 'twitter', author: '', tweetId: '' });
  const [actionStates, setActionStates] = useState({});

  const setActionState = (itemId, actionType, state) => {
    setActionStates(prev => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [actionType]: state } }));
  };

  const buildActionGuidance = (item, actionType) => {
    const parts = [];

    parts.push(`## RECOMMENDED ACTION: ${actionType.replace(/_/g, ' ').toUpperCase()}`);
    if (item.action?.reasoning) parts.push(`Reasoning: ${item.action.reasoning}`);

    if (item.is_brand_at_fault) parts.push('IMPORTANT: Our brand is at fault in this situation. Respond with accountability.');
    if (item.is_specific_complaint) parts.push('This is a specific customer complaint directed at us.');
    if (item.brand_relevance) parts.push(`Brand relevance: ${item.brand_relevance}`);

    if (item.content_frame) parts.push(`Content frame: ${item.content_frame} (this is a ${item.content_frame} post)`);
    if (item.dominant_emotion) parts.push(`Dominant emotion: ${item.dominant_emotion}`);
    if (item.sentiment_score != null) {
      const label = item.sentiment_score > 0.3 ? 'very positive' : item.sentiment_score > 0 ? 'slightly positive' : item.sentiment_score > -0.3 ? 'slightly negative' : 'very negative';
      parts.push(`Sentiment: ${label} (${item.sentiment_score.toFixed(2)})`);
    }

    if (item.urgency_window) parts.push(`Urgency: ${item.urgency_window}`);
    if (item.crisis_markers?.length > 0) parts.push(`Crisis markers: ${item.crisis_markers.join(', ')}`);
    if (item.legal_indicators?.length > 0) parts.push(`Legal indicators: ${item.legal_indicators.join(', ')}`);

    const likes = item.engagement?.likes || 0;
    const comments = item.engagement?.comments || 0;
    if (likes > 100 || comments > 20) {
      parts.push(`Engagement: ${likes} likes, ${comments} comments — this is HIGH VISIBILITY, be careful and professional`);
    }
    if (item.reach_tier && item.reach_tier !== 'unknown') {
      parts.push(`Author reach: ${item.reach_tier} (${item.author_followers || 0} followers)`);
    }

    if (item.action?.suggested_steps?.length > 0) {
      parts.push('\n## STEPS TO FOLLOW:');
      item.action.suggested_steps.forEach((step, i) => { parts.push(`${i + 1}. ${step}`); });
    }

    if (item.action?.context_summary) parts.push(`\nSituation context: ${item.action.context_summary}`);
    if (item.narrative_title) parts.push(`Part of narrative: "${item.narrative_title}"`);
    if (item.relevance_reason) parts.push(`Why relevant: ${item.relevance_reason}`);
    if (item.escalation_hint) parts.push(`Escalation note: ${item.escalation_hint}`);

    if (item.controversy_level && item.controversy_level !== 'low') parts.push(`Controversy level: ${item.controversy_level} — tread carefully`);
    if (item.velocity_assessment === 'accelerating') parts.push('VELOCITY: This is accelerating/going viral. Time-sensitive response needed.');

    return parts.join('\n');
  };

  const handleAction = async (item, actionType) => {
    if (!actionType) return;
    const normalized = actionType.toLowerCase().replace(/\s+/g, '_');

    if (REACTION_ACTIONS.has(normalized)) return handleReactionAction(item, normalized);
    if (normalized === 'monitor') return handleMonitorAction(item);
    if (normalized === 'report') return handleReportAction(item);
    if (AMPLIFY_ACTIONS.has(normalized)) return handleExternalUrlAction(item);
    if (normalized === 'escalate') return handleEscalate(item.id);
    if (normalized === 'ignore') return handleDismiss(item.id);
    if (INFORMATIONAL_ACTIONS.has(normalized)) return handleInformationalAction(item, normalized);
    handleReactionAction(item, normalized);
  };

  const handleReactionAction = async (item, actionType) => {
    const itemOrgId = orgId || item.org_id;
    setReactionModal({ visible: true, loading: true, text: '', explanation: '', error: null, platform: item.source_platform || 'twitter', author: item.author_username || '', tweetId: item.source_post_id || '' });
    setActionState(item.id, actionType, 'loading');
    try {
      const guidance = buildActionGuidance(item, actionType);
      const tweetContent = item.post_content || item.content_snippet || item.headline || '';
      const tweetAuthor = item.author_username || 'unknown';
      const result = await ListsManager.generateTweetReaction(tweetContent, tweetAuthor, null, null, item.list_id || null, guidance, itemOrgId);
      if (result?.success) {
        setReactionModal(prev => ({ ...prev, loading: false, text: result.reaction_text || '', explanation: result.explanation || '' }));
        setActionState(item.id, actionType, 'done');
        ListsManager.recordSignalFeedAction(item.id, actionType, itemOrgId);
      } else {
        setReactionModal(prev => ({ ...prev, loading: false, error: result?.error_message || 'Failed to generate reaction' }));
        setActionState(item.id, actionType, null);
      }
    } catch (e) {
      setReactionModal(prev => ({ ...prev, loading: false, error: e.message || 'Network error' }));
      setActionState(item.id, actionType, null);
    }
  };

  const handleMonitorAction = async (item) => {
    const itemOrgId = orgId || item.org_id;
    setActionState(item.id, 'monitor', 'loading');
    try {
      const body = {
        tracker_type: 'saved_content',
        schedule_pattern: 'passive',
        title: `Tracking: ${(item.headline || item.content_snippet || 'Signal').slice(0, 80)}`,
        source_post_id: item.source_post_id || undefined,
        source_platform: item.source_platform || undefined,
        source_url: item.source_url || undefined,
        narrative_id: item.narrative_id || undefined,
      };
      if (itemOrgId) body.org_id = itemOrgId;
      const result = await ListsManager.createTracker(body);
      if (result) {
        setActionState(item.id, 'monitor', 'done');
        ListsManager.recordSignalFeedAction(item.id, 'monitor', itemOrgId);
      } else {
        setActionState(item.id, 'monitor', null);
      }
    } catch (e) {
      setActionState(item.id, 'monitor', null);
    }
  };

  const handleReportAction = (item) => {
    const platform = item.source_platform || '';
    const violationType = item.violation_type || '';
    const platformUrls = REPORT_URLS[platform];
    let url;
    if (platformUrls) {
      if (violationType.includes('copyright')) url = platformUrls.copyright;
      else if (violationType.includes('trademark')) url = platformUrls.trademark;
      else url = platformUrls.default;
    } else {
      url = item.source_url;
    }
    if (url) window.open(url, '_blank');
    setActionState(item.id, 'report', 'done');
    const itemOrgId = orgId || item.org_id;
    ListsManager.recordSignalFeedAction(item.id, 'report', itemOrgId);
  };

  const handleExternalUrlAction = (item) => {
    if (item.source_url) window.open(item.source_url, '_blank');
    setActionState(item.id, 'amplify', 'done');
    const itemOrgId = orgId || item.org_id;
    ListsManager.recordSignalFeedAction(item.id, 'amplify', itemOrgId);
  };

  const handleInformationalAction = async (item, actionType) => {
    const text = item.action?.context_summary || item.relevance_reason || item.headline || item.content_snippet || '';
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) { /* fallback: no-op */ }
    setActionState(item.id, actionType, 'done');
    const itemOrgId = orgId || item.org_id;
    ListsManager.recordSignalFeedAction(item.id, actionType, itemOrgId);
  };

  const fetchCategories = useCallback(async () => {
    try {
      const res = await ListsManager.fetchSignalCategories(orgId);
      if (res?.categories) setCategories(res.categories);
    } catch (e) {
      console.error('[SignalFeed] categories error:', e);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  // Strategic patterns — resolve signal pattern_ids to human-readable titles.
  const fetchPatterns = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await ListsManager.fetchStrategyPatterns(orgId);
      if (res?.patterns) {
        const map = {};
        res.patterns.forEach(p => { if (p.id) map[p.id] = { title: p.title, pattern_type: p.pattern_type }; });
        setPatternMap(map);
      }
    } catch (e) {
      console.error('[SignalFeed] patterns error:', e);
    }
  }, [orgId]);

  const fetchFeed = useCallback(async () => {
    if (!selectedCategory) return;
    setFeedLoading(true);
    try {
      const res = await ListsManager.fetchSignalFeed(orgId, selectedCategory, { sort: 'priority', limit: 50 });
      if (res?.items) {
        setFeedItems(res.items); setTotal(res.total || 0);
      }
    } catch (e) {
      console.error('[SignalFeed] feed error:', e);
    } finally {
      setFeedLoading(false);
    }
  }, [orgId, selectedCategory]);

  const applySort = (arr) => {
    return [...arr].sort((a, b) => {
      let valA, valB;
      if (sortKey === 'date') {
        valA = new Date(a.created_at || 0).getTime();
        valB = new Date(b.created_at || 0).getTime();
      } else if (sortKey === 'engagement') {
        const eng = (x) => {
          const e = x.engagement || {};
          return Math.log1p(e.likes || 0) * 2 +
            Math.log1p(e.comments || 0) * 3 +
            Math.log1p(e.shares || 0) * 4 +
            Math.log1p(x.author_followers || 0) * 1.5;
        };
        valA = eng(a);
        valB = eng(b);
      } else {
        valA = a.priority_score || 0;
        valB = b.priority_score || 0;
      }
      return sortDir === 'desc' ? valB - valA : valA - valB;
    });
  };

  const sortedFeedItems = applySort(feedItems);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchPatterns(); }, [fetchPatterns]);
  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const handleDismiss = async (itemId) => {
    try {
      const item = feedItems.find(i => i.id === itemId);
      const itemOrgId = orgId || item?.org_id;
      await ListsManager.updateSignalFeedStatus(itemId, 'dismissed', itemOrgId);
      setFeedItems(prev => prev.filter(i => i.id !== itemId));
      if (selectedItem?.id === itemId) setSelectedItem(null);
      fetchCategories();
    } catch (e) { console.error('Dismiss failed:', e); }
  };

  const handleEscalate = async (itemId) => {
    try {
      const item = feedItems.find(i => i.id === itemId);
      const itemOrgId = orgId || item?.org_id;
      await ListsManager.escalateSignalFeedItem(itemId, itemOrgId);
      setFeedItems(prev => prev.map(i =>
        i.id === itemId ? { ...i, is_escalated: true, escalation_reason: 'Manually escalated' } : i
      ));
      fetchCategories();
    } catch (e) { console.error('Escalate failed:', e); }
  };

  const startDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = sidebarW;
    const onMove = (ev) => {
      let dx = ev.clientX - startX;
      if (isRTL) dx = -dx;
      setSidebarW(Math.max(300, Math.min(500, startW + dx)));
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const urgentCount = feedItems.filter(i => i.urgency_window === 'immediate').length;
  const newCount = feedItems.filter(i => i.status === 'unreviewed').length;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: T.bg }}>
        <ActivityIndicator size="large" color={T.primary} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: T.bg, overflow: 'hidden', direction: isRTL ? 'rtl' : 'ltr' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        .sf-item { transition: background 0.15s ease; cursor: pointer; position: relative; border-radius: 10px; }
        .sf-item:hover { background: ${T.surface} !important; }
        .sf-item:hover .sf-quick-actions { opacity: 1; pointer-events: auto; }
        .sf-item.sel { background: ${T.primary}08 !important; }
        .sf-pill { cursor: pointer; transition: all 0.15s ease; border: none; outline: none; }
        .sf-pill:hover { background: ${T.surface} !important; }
        .sf-divider { width: 1px; flex-shrink: 0; cursor: col-resize; background: ${T.border}; transition: background 0.15s; }
        .sf-divider:hover { background: ${T.primary}; }
        .sf-scroll::-webkit-scrollbar { width: 4px; }
        .sf-scroll::-webkit-scrollbar-track { background: transparent; }
        .sf-scroll::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.12); border-radius: 2px; }
        .sf-scroll::-webkit-scrollbar-thumb:hover { background: rgba(128,128,128,0.25); }
        .sf-quick-actions { opacity: 0; pointer-events: none; transition: opacity 0.15s ease; }
        @keyframes subtlePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .fa-spin { animation: fa-spin 1s linear infinite; }
        @keyframes fa-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .sf-tab-toggle { display: inline-flex; border-radius: 8px; background: ${T.surface}; padding: 3px; border: 1px solid ${T.border}; }
        .sf-tab-btn { border: none; outline: none; cursor: pointer; padding: 7px 16px; border-radius: 6px; font-size: 12px; font-weight: 600; transition: all 0.15s; background: transparent; color: ${T.textMuted}; }
        .sf-tab-btn.active { background: ${T.card}; color: ${T.text}; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
      ` }} />

      {/* Top toggle: Strategy / Signals */}
      <div style={{ padding: '12px 24px', borderBottom: `1px solid ${T.border}`, background: T.card, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div className="sf-tab-toggle">
          <button className={`sf-tab-btn ${activeTab === 'strategy' ? 'active' : ''}`} onClick={() => setActiveTab('strategy')}>
            <FaChartLine size={10} style={{ marginRight: 6 }} />
            {_t('app.signalFeed.tabStrategy', { defaultValue: 'Strategy' })}
          </button>
          <button className={`sf-tab-btn ${activeTab === 'signals' ? 'active' : ''}`} onClick={() => setActiveTab('signals')}>
            <FaBolt size={10} style={{ marginRight: 6 }} />
            {_t('app.signalFeed.tabSignals', { defaultValue: 'Signals' })}
          </button>
        </div>
      </div>

      {/* Strategy tab */}
      {activeTab === 'strategy' && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          <OrgStrategyScreen orgId={orgId} />
        </div>
      )}

      {/* Signals tab */}
      {activeTab === 'signals' && <>
      {/* Status summary + Category pills */}
      <div style={{ borderBottom: `1px solid ${T.border}`, background: T.card }}>
        {/* Status line */}
        {(urgentCount > 0 || newCount > 0) && (
          <div style={{ padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 16, borderBottom: `1px solid ${T.border}` }}>
            {urgentCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: 3, background: '#ff453a', animation: 'subtlePulse 2s ease-in-out infinite' }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{urgentCount} {_t('app.signalFeed.needAttention', { defaultValue: 'need attention' })}</span>
              </div>
            )}
            {newCount > 0 && (
              <span style={{ fontSize: 12, color: T.textMuted }}>{newCount} {_t('app.signalFeed.newToday', { defaultValue: 'unreviewed' })}</span>
            )}
          </div>
        )}

        {/* Category pills */}
        <div style={{ display: 'flex', gap: 4, padding: '12px 24px', overflowX: 'auto', scrollbarWidth: 'none' }}>
          <button
            className="sf-pill"
            onClick={() => { setSelectedCategory('all'); setSelectedItem(null); }}
            style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: selectedCategory === 'all' ? 600 : 400,
              background: selectedCategory === 'all' ? `${T.primary}10` : 'transparent',
              color: selectedCategory === 'all' ? T.primary : T.textMuted,
            }}
          >
            {_t('app.signalFeed.cat_all', { defaultValue: 'All' })}
          </button>
          <button
            className="sf-pill"
            onClick={() => { setSelectedCategory('to_report'); setSelectedItem(null); }}
            style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: selectedCategory === 'to_report' ? 600 : 400,
              background: selectedCategory === 'to_report' ? 'rgba(255,69,58,0.06)' : 'transparent',
              color: selectedCategory === 'to_report' ? '#ff453a' : T.textMuted,
            }}
          >
            {_t('app.signalFeed.cat_to_report', { defaultValue: 'To Report' })}
          </button>
          {categories.map((cat) => {
            const config = CATEGORY_CONFIG[cat.category] || CATEGORY_CONFIG.fires;
            const Icon = config.icon;
            const isSelected = selectedCategory === cat.category;
            const count = cat.unreviewed_count || cat.total_count || 0;

            return (
              <button
                key={cat.category}
                className="sf-pill"
                onClick={() => { setSelectedCategory(cat.category); setSelectedItem(null); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 8, fontSize: 12, flexShrink: 0,
                  fontWeight: isSelected ? 600 : 400,
                  background: isSelected ? `${T.primary}10` : 'transparent',
                  color: isSelected ? T.primary : T.textMuted,
                }}
              >
                <Icon size={11} />
                <span>{_t(`app.signalFeed.cat_${cat.category}`)}</span>
                {count > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: isSelected ? T.primary : T.textMuted }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main 3-panel content */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Feed list panel */}
        <div style={{ width: sidebarW, minWidth: 300, maxWidth: 500, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Sort bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', borderBottom: `1px solid ${T.border}` }}>
            {SORT_OPTIONS.map(opt => {
              const isActive = sortKey === opt.key;
              return (
                <div
                  key={opt.key}
                  onClick={() => {
                    if (isActive) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                    else { setSortKey(opt.key); setSortDir(opt.defaultDir); }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 3,
                    padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: isActive ? 600 : 400,
                    cursor: 'pointer',
                    background: isActive ? T.surface : 'transparent',
                    color: isActive ? T.text : T.textMuted,
                    transition: 'all 0.15s ease',
                  }}
                >
                  {_t(`app.signalFeed.${opt.labelKey}`, { defaultValue: opt.defaultLabel })}
                  {isActive && (sortDir === 'desc' ? <FaArrowDown size={8} /> : <FaArrowUp size={8} />)}
                </div>
              );
            })}
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: T.textMuted }}>{total}</span>
          </div>

          {/* Feed items */}
          <div className="sf-scroll" style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
            {feedLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <ActivityIndicator size="small" color={T.primary} />
              </div>
            ) : sortedFeedItems.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, color: T.textMuted, fontSize: 13, gap: 8 }}>
                <FaChartBar size={20} color={T.border} />
                {_t('app.signalFeed.emptyCategory')}
              </div>
            ) : (
              sortedFeedItems.map(item => (
                <ListItem
                  key={item.id}
                  item={item}
                  isSelected={selectedItem?.id === item.id}
                  onSelect={setSelectedItem}
                  onDismiss={handleDismiss}
                  isRTL={isRTL}
                />
              ))
            )}
          </div>
        </div>

        <div className="sf-divider" onMouseDown={startDrag} />

        {/* Detail panel */}
        <div className="sf-scroll" style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
          {!selectedItem ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
              <FaChartBar size={24} color={T.border} />
              <span style={{ color: T.textMuted, fontSize: 13 }}>{_t('app.signalFeed.selectSignal')}</span>
            </div>
          ) : (
            <SignalDetail
              item={selectedItem}
              onDismiss={handleDismiss}
              onEscalate={handleEscalate}
              onAction={handleAction}
              actionStates={actionStates}
              patternMap={patternMap}
              onClose={() => setSelectedItem(null)}
              isRTL={isRTL}
            />
          )}
        </div>

        {/* Media pane */}
        {selectedItem && (
          <>
            <div style={{ width: 1, flexShrink: 0, background: T.border }} />
            <div className="sf-scroll" style={{ width: 340, flexShrink: 0, overflowY: 'auto', background: T.bg }}>
              <MediaPane item={selectedItem} isRTL={isRTL} />
            </div>
          </>
        )}
      </div>

      </>}

      <ReactionModal
        isVisible={reactionModal.visible}
        onClose={() => setReactionModal(prev => ({ ...prev, visible: false }))}
        reactionText={reactionModal.text}
        explanation={reactionModal.explanation}
        isLoading={reactionModal.loading}
        error={reactionModal.error}
        platform={reactionModal.platform}
        originalAuthor={reactionModal.author}
        tweetId={reactionModal.tweetId}
        onRegenerate={() => {
          if (selectedItem) {
            const actionType = selectedItem.action?.recommended_action || selectedItem.light_suggestion?.[0] || 'react';
            handleReactionAction(selectedItem, actionType);
          }
        }}
      />
    </div>
  );
}

// ── ListItem (feed card) ─────────────────────────────────────────────────

function ListItem({ item, isSelected, onSelect, onDismiss, isRTL }) {
  const actionVerb = getActionVerb(item);
  const isImmediate = item.urgency_window === 'immediate';
  const isHours = item.urgency_window === 'hours';
  const PlatformIcon = PLATFORM_ICONS[item.source_platform] || FaGlobe;
  const timeframe = isImmediate ? _t('app.signalFeed.now', { defaultValue: 'now' }) : isHours ? _t('app.signalFeed.withinHours', { defaultValue: 'within hours' }) : '';

  const borderColor = isImmediate ? '#ff453a' : isHours ? '#f59e0b' : 'transparent';
  const bgTint = isImmediate ? 'rgba(255,69,58,0.03)' : isHours ? 'rgba(245,158,11,0.02)' : undefined;

  const headlineText = item.headline || item.ai_summary || item.content_snippet || '';

  return (
    <div
      className={`sf-item ${isSelected ? 'sel' : ''}`}
      onClick={() => onSelect(item)}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6,
        padding: '14px 16px', marginBottom: 4,
        borderLeft: isRTL ? 'none' : `3px solid ${borderColor}`,
        borderRight: isRTL ? `3px solid ${borderColor}` : 'none',
        background: bgTint,
      }}
    >
      {/* WHAT HAPPENED — bold headline */}
      <p style={{
        fontSize: 14, fontWeight: 700, lineHeight: 1.45, color: T.text, margin: 0,
        overflow: 'hidden', textOverflow: 'ellipsis',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        direction: 'auto', letterSpacing: -0.1,
      }}>
        {headlineText}
      </p>

      {/* WHAT TO DO — action line */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: T.primary }}>
          → {actionVerb}
        </span>
        {timeframe && (
          <span style={{ fontSize: 11, color: T.textMuted }}>· {timeframe}</span>
        )}
      </div>

      {/* Strategic note preview */}
      {item.strategic_note && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <FaChartLine size={8} color={T.primary} style={{ flexShrink: 0, opacity: 0.8 }} />
          <span style={{ fontSize: 11, lineHeight: 1.4, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: 'auto' }}>
            {item.strategic_note}
          </span>
        </div>
      )}

      {/* META — author, platform, time, engagement */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
        <PlatformIcon size={9} color={T.textMuted} />
        <span style={{ fontSize: 11, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          @{item.author_username || 'unknown'}
        </span>
        <span style={{ fontSize: 11, color: T.textMuted }}>·</span>
        <span style={{ fontSize: 11, color: T.textMuted }}>{timeAgo(item.created_at)}</span>
        {(item.engagement?.likes || 0) > 100 && (
          <>
            <span style={{ fontSize: 11, color: T.textMuted }}>·</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: T.textMuted }}>
              <FaHeart size={7} /> {fmtNum(item.engagement.likes)}
            </span>
          </>
        )}
        {(item.engagement?.comments || 0) > 10 && (
          <>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: T.textMuted }}>
              <FaComment size={7} /> {fmtNum(item.engagement.comments)}
            </span>
          </>
        )}
      </div>

      {/* Hover quick actions */}
      <div className="sf-quick-actions" style={{
        position: 'absolute', top: 10, [isRTL ? 'left' : 'right']: 10,
        display: 'flex', gap: 4,
      }}>
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(item.id); }}
          style={{
            width: 24, height: 24, borderRadius: 6, border: `1px solid ${T.border}`,
            background: T.card, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
          title={_t('app.signalFeed.dismiss', { defaultValue: 'Dismiss' })}
        >
          <FaTimes size={9} color={T.textMuted} />
        </button>
      </div>
    </div>
  );
}

// ── SignalDetail (center panel) ─────────────────────────────────────────

function ActionButton({ item, actionType, label, isPrimary, onAction, actionStates }) {
  const state = actionStates?.[item.id]?.[actionType];
  const ActionIcon = ACTION_ICONS[actionType] || FaBolt;

  if (state === 'done') {
    return (
      <button style={{
        padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
        background: 'rgba(48,209,88,0.1)', color: '#30d158', border: '1px solid rgba(48,209,88,0.2)', cursor: 'default',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <FaCheck size={10} /> {_t('app.signalFeed.done', { defaultValue: 'Done' })}
      </button>
    );
  }

  if (state === 'loading') {
    return (
      <button style={{
        padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
        background: isPrimary ? T.primary : 'transparent', color: isPrimary ? '#fff' : T.textSec,
        border: isPrimary ? 'none' : `1px solid ${T.border}`, cursor: 'wait',
        display: 'flex', alignItems: 'center', gap: 6, opacity: 0.7,
      }}>
        <FaSpinner size={10} className="fa-spin" /> {label}
      </button>
    );
  }

  return (
    <button
      onClick={() => onAction(item, actionType)}
      style={{
        padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: isPrimary ? 600 : 500,
        background: isPrimary ? T.primary : 'transparent', color: isPrimary ? '#fff' : T.textSec,
        border: isPrimary ? 'none' : `1px solid ${T.border}`, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6,
      }}
    >
      <ActionIcon size={10} /> {label}
    </button>
  );
}

function SignalDetail({ item, onDismiss, onEscalate, onAction, actionStates, patternMap = {}, onClose, isRTL }) {
  // Intelligence section is always shown now (toggle removed).
  const [intelligenceOpen] = useState(true);

  if (!item) return null;

  // Resolve linked strategic patterns (pattern_ids) to readable titles.
  const linkedPatterns = (item.pattern_ids || [])
    .map(id => patternMap[id])
    .filter(Boolean);

  const hasStrategicInsight = item.strategic_note || item.priority_alignment || linkedPatterns.length > 0;

  const hasAction = item.action && item.action.title;
  const actionVerb = getActionVerb(item);
  const likes = item.engagement?.likes || 0;
  const comments = item.engagement?.comments || 0;
  const shares = item.engagement?.shares || 0;
  const views = item.engagement?.views || 0;

  const headlineText = item.headline || item.ai_summary || item.content_snippet || '';
  const summaryText = item.ai_summary || item.content_snippet || item.post_content?.slice(0, 300) || '';
  const PlatformIcon = PLATFORM_ICONS[item.source_platform] || FaGlobe;

  return (
    <div style={{ padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {item.urgency_window === 'immediate' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: 3, background: '#ff453a', animation: 'subtlePulse 2s ease-in-out infinite' }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: '#ff453a' }}>{_t('app.signalFeed.urgencyImmediate', { defaultValue: 'Immediate' })}</span>
            </div>
          )}
          {item.urgency_window === 'hours' && (
            <span style={{ fontSize: 11, fontWeight: 500, color: '#f59e0b' }}>{_t('app.signalFeed.urgencyHours', { defaultValue: 'Within hours' })}</span>
          )}
          <span style={{ fontSize: 11, color: T.textMuted }}>{timeAgo(item.created_at)}</span>
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            width: 28, height: 28, borderRadius: 8, border: `1px solid ${T.border}`,
            background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}>
            <FaTimes size={10} color={T.textMuted} />
          </button>
        )}
      </div>

      {/* ═══ WHAT HAPPENED ═══ */}
      <div style={{ padding: '20px 24px', borderRadius: 12, background: T.surface, border: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: T.textMuted, marginBottom: 10 }}>
          {_t('app.signalFeed.whatHappened', { defaultValue: 'What happened' })}
        </div>

        <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: T.text, lineHeight: 1.4, letterSpacing: -0.3 }}>
          {headlineText}
        </h2>

        {summaryText !== headlineText && (
          <p style={{ margin: '0 0 12px', fontSize: 14, lineHeight: 1.6, color: T.textSec, direction: 'auto' }}>
            {summaryText}
          </p>
        )}

        {/* Author meta */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <PlatformIcon size={11} color={T.textMuted} />
          <span style={{ fontSize: 12, color: T.textMuted }}>@{item.author_username || 'unknown'}</span>
          {item.author_followers > 0 && (
            <span style={{ fontSize: 12, color: T.textMuted }}>· {fmtNum(item.author_followers)} followers</span>
          )}
          <span style={{ fontSize: 12, color: T.textMuted }}>· {timeAgo(item.created_at)}</span>
        </div>

        {/* Crisis & Legal markers */}
        {((item.crisis_markers && item.crisis_markers.length > 0) || (item.legal_indicators && item.legal_indicators.length > 0)) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
            {(item.crisis_markers || []).map(m => (
              <span key={m} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 11, fontWeight: 500, padding: '4px 8px', borderRadius: 6,
                background: 'rgba(255,69,58,0.05)', color: '#ff453a',
              }}>
                <FaExclamationTriangle size={9} /> {m.replace(/_/g, ' ')}
              </span>
            ))}
            {(item.legal_indicators || []).map(m => (
              <span key={m} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 11, fontWeight: 500, padding: '4px 8px', borderRadius: 6,
                background: 'rgba(191,90,242,0.05)', color: '#bf5af2',
              }}>
                <FaGavel size={9} /> {m.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ═══ WHAT TO DO ═══ */}
      <div style={{ padding: '20px 24px', borderRadius: 12, background: T.surface, border: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: T.textMuted, marginBottom: 10 }}>
          {_t('app.signalFeed.whatToDo', { defaultValue: 'What to do' })}
        </div>

        {hasAction ? (
          <>
            <h3 style={{ margin: '0 0 10px', fontSize: 17, fontWeight: 600, color: T.text, lineHeight: 1.4, letterSpacing: -0.2 }}>
              {item.action.title}
            </h3>
            {item.action.reasoning && (
              <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.6, color: T.textSec }}>
                {item.action.reasoning}
              </p>
            )}
            {item.action.suggested_steps?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {item.action.suggested_steps.map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderTop: i > 0 ? `1px solid ${T.border}` : 'none' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.primary, width: 18, flexShrink: 0 }}>{i + 1}.</span>
                    <span style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>{step}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <ActionButton
                item={item}
                actionType={item.action.recommended_action}
                label={actionLabel(item.action.recommended_action) || actionVerb}
                isPrimary
                onAction={onAction}
                actionStates={actionStates}
              />
              {item.action.alternative_action && (
                <ActionButton
                  item={item}
                  actionType={item.action.alternative_action}
                  label={actionLabel(item.action.alternative_action)}
                  isPrimary={false}
                  onAction={onAction}
                  actionStates={actionStates}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 600, color: T.text, lineHeight: 1.4 }}>
              {actionVerb}
            </h3>
            {item.relevance_reason && (
              <p style={{ margin: '0 0 12px', fontSize: 13, color: T.textSec, lineHeight: 1.5 }}>
                {item.relevance_reason}
              </p>
            )}
            {item.light_suggestion && item.light_suggestion.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                {item.light_suggestion.map((s, i) => (
                  <ActionButton
                    key={i}
                    item={item}
                    actionType={s}
                    label={actionLabel(s)}
                    isPrimary={i === 0}
                    onAction={onAction}
                    actionStates={actionStates}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══ STRATEGIC INSIGHT (under What to do) ═══ */}
      {hasStrategicInsight && (
        <div style={{ padding: '18px 22px', borderRadius: 12, background: 'linear-gradient(135deg, rgba(10,132,255,0.05), rgba(94,92,230,0.05))', border: `1px solid rgba(10,132,255,0.15)` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: item.strategic_note ? 10 : 0 }}>
            <FaChartLine size={11} color={T.primary} />
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: T.primary }}>
              {_t('app.signalFeed.strategicInsight', { defaultValue: 'Strategic Insight' })}
            </span>
          </div>
          {item.strategic_note && (
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: T.text, direction: 'auto' }}>
              {item.strategic_note}
            </p>
          )}
          {(item.priority_alignment || linkedPatterns.length > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: item.strategic_note ? 12 : 0 }}>
              {item.priority_alignment && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: T.primary, padding: '4px 10px', borderRadius: 20, background: `${T.primary}12` }}>
                  <FaCrosshairs size={9} /> {item.priority_alignment}
                </span>
              )}
              {linkedPatterns.map((p, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, color: '#bf5af2', padding: '4px 10px', borderRadius: 20, background: 'rgba(191,90,242,0.1)' }}>
                  <FaBolt size={9} /> {p.title || (p.pattern_type || '').replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Context chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {item.content_frame && (
          <span style={{ fontSize: 11, fontWeight: 500, color: T.textMuted, padding: '3px 8px', borderRadius: 5, background: T.surface }}>
            {_t(`app.signalFeed.frame_${item.content_frame}`, { defaultValue: FRAME_LABELS[item.content_frame] || item.content_frame })}
          </span>
        )}
        {item.brand_relevance && (
          <span style={{ fontSize: 11, fontWeight: 500, color: T.textMuted, padding: '3px 8px', borderRadius: 5, background: T.surface }}>
            {_t(`app.signalFeed.relevance_${item.brand_relevance}`, { defaultValue: item.brand_relevance })}
          </span>
        )}
        {item.velocity_assessment && item.velocity_assessment !== 'unknown' && item.velocity_assessment !== 'stable' && (
          <span style={{ fontSize: 11, fontWeight: 500, color: item.velocity_assessment === 'accelerating' ? '#ff453a' : T.textMuted, padding: '3px 8px', borderRadius: 5, background: T.surface }}>
            {item.velocity_assessment === 'accelerating' && <FaArrowUp size={8} style={{ marginRight: 3 }} />}
            {_t(`app.signalFeed.velocity_${item.velocity_assessment}`, { defaultValue: item.velocity_assessment })}
          </span>
        )}
        {item.controversy_level && item.controversy_level !== 'low' && (
          <span style={{ fontSize: 11, fontWeight: 500, color: T.textMuted, padding: '3px 8px', borderRadius: 5, background: T.surface }}>
            {_t(`app.signalFeed.controversy_${item.controversy_level}`, { defaultValue: item.controversy_level })} {_t('app.signalFeed.controversySuffix', { defaultValue: 'controversy' })}
          </span>
        )}
      </div>

      {/* Engagement row */}
      {(likes > 0 || comments > 0 || shares > 0 || views > 0) && (
        <div style={{ display: 'flex', gap: 16 }}>
          {likes > 0 && <MetricItem icon={FaHeart} value={fmtNum(likes)} label={_t('app.signalFeed.likes', { defaultValue: 'likes' })} />}
          {comments > 0 && <MetricItem icon={FaComment} value={fmtNum(comments)} label={_t('app.signalFeed.comments', { defaultValue: 'comments' })} />}
          {shares > 0 && <MetricItem icon={FaRetweet} value={fmtNum(shares)} label={_t('app.signalFeed.shares', { defaultValue: 'shares' })} />}
          {views > 0 && <MetricItem icon={FaEye} value={fmtNum(views)} label={_t('app.signalFeed.views', { defaultValue: 'views' })} />}
        </div>
      )}

      {/* Intelligence section (always visible) */}
      <div>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: 0,
            fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5,
          }}
        >
          <FaChartBar size={10} />
          {_t('app.signalFeed.intelligence', { defaultValue: 'Intelligence' })}
        </div>

        {intelligenceOpen && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Sentiment */}
            {item.sentiment_score != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: T.textMuted, width: 70 }}>{_t('app.signalFeed.sentiment', { defaultValue: 'Sentiment' })}</span>
                <div style={{ flex: 1, height: 4, borderRadius: 2, background: T.surface, position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: 1, background: T.border }} />
                  {item.sentiment_score !== 0 && (
                    <div style={{
                      position: 'absolute', top: 0, bottom: 0, borderRadius: 2,
                      left: item.sentiment_score >= 0 ? '50%' : `${50 - Math.abs(item.sentiment_score) * 50}%`,
                      width: `${Math.abs(item.sentiment_score) * 50}%`,
                      background: item.sentiment_score > 0 ? '#30d158' : '#ff453a',
                    }} />
                  )}
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.textSec, width: 35, textAlign: 'right' }}>
                  {item.sentiment_score > 0 ? '+' : ''}{(item.sentiment_score * 100).toFixed(0)}
                </span>
              </div>
            )}

            {/* Reach */}
            {item.reach_tier && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: T.textMuted, width: 70 }}>{_t('app.signalFeed.reach', { defaultValue: 'Reach' })}</span>
                <span style={{ fontSize: 12, fontWeight: 500, color: T.text }}>
                  {_t(`app.signalFeed.reach_${item.reach_tier}`, { defaultValue: item.reach_tier })} {item.author_followers > 0 && `(${fmtNum(item.author_followers)})`}
                </span>
              </div>
            )}

            {/* Emotion */}
            {item.dominant_emotion && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: T.textMuted, width: 70 }}>{_t('app.signalFeed.emotionLabel', { defaultValue: 'Emotion' })}</span>
                <span style={{ fontSize: 12, fontWeight: 500, color: T.text }}>{_t(`app.signalFeed.emotion_${item.dominant_emotion}`, { defaultValue: item.dominant_emotion })}</span>
              </div>
            )}

            {/* Escalation hint */}
            {item.escalation_hint && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: T.textMuted, width: 70 }}>{_t('app.signalFeed.whyLabel', { defaultValue: 'Why' })}</span>
                <span style={{ fontSize: 12, color: T.textSec }}>{item.escalation_hint}</span>
              </div>
            )}

            {/* Engagement opportunity */}
            {item.engagement_opportunity && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: T.textMuted, width: 70 }}>{_t('app.signalFeed.opportunityLabel', { defaultValue: 'Opportunity' })}</span>
                <span style={{ fontSize: 12, color: T.textSec }}>{item.engagement_opportunity.type?.replace(/_/g, ' ')}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action footer */}
      <div style={{ display: 'flex', gap: 10, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
        {!item.is_escalated && onEscalate && (
          <button
            onClick={() => onEscalate(item.id)}
            style={{
              padding: '9px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              background: 'rgba(255,69,58,0.06)', color: '#ff453a',
              border: '1px solid rgba(255,69,58,0.15)', cursor: 'pointer',
            }}
          >
            <FaExclamationTriangle size={10} style={{ marginRight: 5 }} />
            {_t('app.signalFeed.escalate', { defaultValue: 'Escalate' })}
          </button>
        )}
        <button
          onClick={() => onDismiss(item.id)}
          style={{
            padding: '9px 16px', borderRadius: 8, fontSize: 12, fontWeight: 500,
            background: 'transparent', color: T.textMuted,
            border: `1px solid ${T.border}`, cursor: 'pointer',
          }}
        >
          {_t('app.signalFeed.dismiss', { defaultValue: 'Dismiss' })}
        </button>
        {item.source_url && (
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5,
              padding: '9px 16px', borderRadius: 8, fontSize: 12, fontWeight: 500,
              background: 'transparent', color: T.textMuted, border: `1px solid ${T.border}`,
              textDecoration: 'none',
            }}
          >
            <FaExternalLinkAlt size={9} /> {_t('app.signalFeed.viewSource', { defaultValue: 'Source' })}
          </a>
        )}
      </div>
    </div>
  );
}

// ── MetricItem ───────────────────────────────────────────────────────────

function MetricItem({ icon: Icon, value, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <Icon size={10} color={T.textMuted} />
      <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{value}</span>
      <span style={{ fontSize: 11, color: T.textMuted }}>{label}</span>
    </div>
  );
}

// ── MediaPane (right side) ──────────────────────────────────────────────

function MediaPane({ item, isRTL }) {
  if (!item) return null;

  const hasContent = item.post_content || item.content_snippet || item.source_url;
  if (!hasContent) return null;

  const platform = item.source_platform;

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Post card */}
      {platform === 'twitter' && <TwitterCard item={item} />}
      {platform === 'tiktok' && <TikTokCard item={item} />}
      {platform === 'telegram' && <TelegramCard item={item} />}
      {platform === 'instagram' && <InstagramCard item={item} />}
      {platform !== 'twitter' && platform !== 'tiktok' && platform !== 'telegram' && platform !== 'instagram' && (
        <GenericPostCard item={item} />
      )}

      {item.author_username && <AuthorCard item={item} />}
    </div>
  );
}

// ── Twitter card ──────────────────────────────────────────────────────────

function TwitterCard({ item }) {
  return (
    <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          {item.author_profile_image ? (
            <img src={item.author_profile_image} alt="" style={{ width: 36, height: 36, borderRadius: 18, objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 36, height: 36, borderRadius: 18, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaTwitter size={14} color="#1da1f2" />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>@{item.author_username || 'unknown'}</div>
            {item.author_followers > 0 && <div style={{ color: T.textMuted, fontSize: 11 }}>{fmtNum(item.author_followers)} followers</div>}
          </div>
          <FaTwitter size={13} color={T.textMuted} style={{ opacity: 0.5 }} />
        </div>
        <p style={{ margin: '0 0 14px', color: T.text, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', direction: 'auto' }}>
          {item.post_content || item.content_snippet || ''}
        </p>
        {item.source_url && (
          <a href={item.source_url} target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            color: T.textMuted, fontSize: 12, fontWeight: 500, textDecoration: 'none',
            padding: '6px 12px', borderRadius: 6, border: `1px solid ${T.border}`,
          }}>
            <FaExternalLinkAlt size={9} /> {_t('app.signalFeed.viewOnX', { defaultValue: 'View on X' })}
          </a>
        )}
      </div>
    </div>
  );
}

// ── TikTok card ───────────────────────────────────────────────────────────

function TikTokCard({ item }) {
  const tiktokId = item.source_url ? (item.source_url.match(/video\/(\d+)/) || [])[1] : null;

  if (tiktokId) {
    return (
      <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
        <iframe
          src={`https://www.tiktok.com/embed/v2/${tiktokId}`}
          style={{ width: '100%', height: 460, border: 'none', display: 'block' }}
          allowFullScreen
          allow="encrypted-media; autoplay"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      </div>
    );
  }

  return (
    <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 18, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FaTiktok size={14} color="#ff0050" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>@{item.author_username || 'unknown'}</div>
          {item.author_followers > 0 && <div style={{ color: T.textMuted, fontSize: 11 }}>{fmtNum(item.author_followers)} followers</div>}
        </div>
      </div>
      <p style={{ margin: 0, color: T.textSec, fontSize: 14, lineHeight: 1.6, direction: 'auto' }}>
        {item.post_content || item.content_snippet || ''}
      </p>
      {item.source_url && (
        <a href={item.source_url} target="_blank" rel="noopener noreferrer" style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 12,
          color: T.textMuted, fontSize: 12, fontWeight: 500, textDecoration: 'none',
          padding: '6px 12px', borderRadius: 6, border: `1px solid ${T.border}`,
        }}>
          <FaExternalLinkAlt size={9} /> {_t('app.signalFeed.watchOnTikTok', { defaultValue: 'Watch on TikTok' })}
        </a>
      )}
    </div>
  );
}

// ── Telegram card ─────────────────────────────────────────────────────────

function TelegramCard({ item }) {
  return (
    <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 18, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FaTelegram size={14} color="#0088cc" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>{item.author_username || 'Channel'}</div>
          {item.author_followers > 0 && <div style={{ color: T.textMuted, fontSize: 11 }}>{fmtNum(item.author_followers)} subscribers</div>}
        </div>
      </div>
      <p style={{ margin: 0, color: T.textSec, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', direction: 'auto' }}>
        {item.post_content || item.content_snippet || ''}
      </p>
      {item.source_url && (
        <a href={item.source_url} target="_blank" rel="noopener noreferrer" style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 12,
          color: T.textMuted, fontSize: 12, fontWeight: 500, textDecoration: 'none',
          padding: '6px 12px', borderRadius: 6, border: `1px solid ${T.border}`,
        }}>
          <FaExternalLinkAlt size={9} /> {_t('app.signalFeed.viewOnTelegram', { defaultValue: 'View on Telegram' })}
        </a>
      )}
    </div>
  );
}

// ── Instagram card ────────────────────────────────────────────────────────

function InstagramCard({ item }) {
  const shortcode = (item.source_url || '').match(/instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/)?.[1] || null;

  if (shortcode) {
    return (
      <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
        <iframe
          src={`https://www.instagram.com/p/${shortcode}/embed/`}
          style={{ width: '100%', height: 460, border: 'none', display: 'block' }}
          allowFullScreen
          scrolling="no"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      </div>
    );
  }

  return (
    <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 18, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FaInstagram size={14} color="#e1306c" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>@{item.author_username || 'instagram'}</div>
          {item.author_followers > 0 && <div style={{ color: T.textMuted, fontSize: 11 }}>{fmtNum(item.author_followers)} followers</div>}
        </div>
      </div>
      <p style={{ margin: 0, color: T.textSec, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', direction: 'auto' }}>
        {item.post_content || item.content_snippet || ''}
      </p>
      {item.source_url && (
        <a href={item.source_url} target="_blank" rel="noopener noreferrer" style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 12,
          color: T.textMuted, fontSize: 12, fontWeight: 500, textDecoration: 'none',
          padding: '6px 12px', borderRadius: 6, border: `1px solid ${T.border}`,
        }}>
          <FaExternalLinkAlt size={9} /> {_t('app.signalFeed.viewOnInstagram', { defaultValue: 'View on Instagram' })}
        </a>
      )}
    </div>
  );
}

// ── Generic post card ─────────────────────────────────────────────────────

function GenericPostCard({ item }) {
  const PlatformIcon = PLATFORM_ICONS[item.source_platform] || FaGlobe;

  return (
    <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 18, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PlatformIcon size={14} color={T.textMuted} />
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>@{item.author_username || 'unknown'}</span>
          {item.author_followers > 0 && <div style={{ color: T.textMuted, fontSize: 11 }}>{fmtNum(item.author_followers)} followers</div>}
        </div>
      </div>
      <p style={{ margin: 0, color: T.textSec, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', direction: 'auto' }}>
        {item.post_content || item.content_snippet || ''}
      </p>
      {item.source_url && (
        <a href={item.source_url} target="_blank" rel="noopener noreferrer" style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 12,
          color: T.textMuted, fontSize: 12, fontWeight: 500, textDecoration: 'none',
          padding: '6px 12px', borderRadius: 6, border: `1px solid ${T.border}`,
        }}>
          <FaExternalLinkAlt size={9} /> {_t('app.signalFeed.viewOriginal', { defaultValue: 'View original' })}
        </a>
      )}
    </div>
  );
}


// ── AuthorCard ────────────────────────────────────────────────────────────

function AuthorCard({ item }) {
  const PlatformIcon = PLATFORM_ICONS[item.source_platform] || FaGlobe;

  const handleClick = () => {
    const username = item.author_username;
    if (!username) return;
    const platform = item.source_platform;
    let url;
    if (platform === 'twitter') url = `https://x.com/${username}`;
    else if (platform === 'tiktok') url = `https://www.tiktok.com/@${username}`;
    else if (platform === 'instagram') url = `https://www.instagram.com/${username}/`;
    else if (platform === 'telegram') url = `https://t.me/${username}`;
    else if (item.source_url) url = item.source_url;
    if (url) window.open(url, '_blank');
  };

  return (
    <div
      onClick={handleClick}
      style={{
        borderRadius: 12, border: `1px solid ${T.border}`, padding: '14px 16px',
        cursor: 'pointer', transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = T.surface; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: T.textMuted, marginBottom: 10 }}>
        {_t('app.signalFeed.author', { defaultValue: 'Author' })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {item.author_profile_image ? (
          <img src={item.author_profile_image} alt="" style={{ width: 32, height: 32, borderRadius: 16, objectFit: 'cover' }} />
        ) : (
          <div style={{ width: 32, height: 32, borderRadius: 16, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PlatformIcon size={12} color={T.textMuted} />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>@{item.author_username}</div>
          {item.author_followers > 0 && (
            <div style={{ fontSize: 11, color: T.textMuted }}>{fmtNum(item.author_followers)} followers</div>
          )}
        </div>
        <FaExternalLinkAlt size={9} color={T.textMuted} />
      </div>
    </div>
  );
}
